import {
	Button,
	Inline,
	Divider,
	Stack,
	Box,
	Text,
	Grid,
	Col,
	Dropdown,
	Icons,
	Spinner,
} from "@sembark-travel/ui-base"
import { useDialog, Dialog } from "@sembark-travel/ui-dialog"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import {
	Form,
	TextInputField,
	withServerErrors,
	SubmissionError,
	SwitchInputField,
	SelectField,
	GetFieldValue,
} from "@sembark-travel/ui-form"
import React, { useMemo } from "react"
import { Optional } from "utility-types"
import { ISupplier, useSupplier } from "./store"
import { FeatureFlag, PERMISSIONS, useCheckPermissions } from "../Auth"
import {
	IPaymentPreference,
	SelectActivityPaymentPreferences,
	SelectHotelPaymentPreferences,
	SelectTransportPaymentPreferences,
} from "../SupplierPaymentPreferences"

type UpdateItemDataSchema = {
	name: string
	supplies_cabs: 0 | 1
	supplies_travel_activities: 0 | 1
	supplies_hotels: 0 | 1
}

type UpdateItemFormProps = {
	initialValues: UpdateItemDataSchema
	onSubmit: (data: UpdateItemDataSchema) => Promise<void>
	onCancel?: () => void
}

type UpdateItemProps = Omit<
	UpdateItemFormProps,
	"onSubmit" | "initialValues"
> & {
	onSuccess: (tsp: ISupplier) => void
	supplier: ISupplier
}

type PaymentPreferencePayload = {
	transport_supplier_payment_preference?: number | null
	activity_supplier_payment_preference?: number | null
	hotel_supplier_payment_preference?: number | null
}

function XHR(xhr: XHRInstance) {
	return {
		async savePaymentPreferance({
			data,
			id,
		}: {
			data: PaymentPreferencePayload
			id: number
		}) {
			return xhr
				.patch(
					`/transport-service-providers/${id}/supplier-payment-preferences`,
					data
				)
				.then((resp) => resp.data.data)
		},
	}
}

export function UpdateItem({
	supplier,
	onSuccess,
	...otherProps
}: UpdateItemProps) {
	const xhr = useXHR()
	const initialValues: UpdateItemDataSchema = useMemo(() => {
		return {
			name: supplier.name,
			supplies_cabs: supplier.supplies_cabs ? 1 : 0,
			supplies_travel_activities: supplier.supplies_travel_activities ? 1 : 0,
			supplies_hotels: supplier.supplies_hotels ? 1 : 0,
		}
	}, [supplier])
	return (
		<UpdateItemForm
			{...otherProps}
			initialValues={initialValues}
			onSubmit={async (data) => {
				const resp = await xhr.post(
					`/transport-service-providers/${supplier.id}`,
					data
				)
				const tsp: ISupplier = resp.data.data
				onSuccess(tsp)
			}}
		/>
	)
}

type UpdateItemInDialogProps = Omit<
	Optional<UpdateItemProps, "onSuccess">,
	"onCancel"
> & {
	children: (props: { edit: () => void }) => React.ReactNode
}

export function UpdateItemInDialog({
	children,
	onSuccess,
	...otherProps
}: UpdateItemInDialogProps) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ edit: open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title="Update Service Provider Details"
				sm
			>
				<Dialog.Body>
					<UpdateItem
						{...otherProps}
						onSuccess={(...args) => {
							onSuccess?.(...args)
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function UpdateItemForm({
	initialValues,
	onSubmit,
	onCancel,
}: Readonly<UpdateItemFormProps>) {
	const { hasPermission } = useCheckPermissions()
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Stack gap="1">
							<Text fontWeight="semibold">Supplied Services</Text>
							<Inline gap="4" flexWrap="wrap">
								{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
									<>
										<Box
											paddingY="1"
											paddingX="4"
											borderWidth="1"
											rounded="md"
											bgColor="subtle"
										>
											<SwitchInputField name="supplies_cabs" label="Cabs" />
										</Box>
										<FeatureFlag flag="travel_activities">
											<Box
												paddingY="1"
												paddingX="4"
												borderWidth="1"
												rounded="md"
												bgColor="subtle"
											>
												<SwitchInputField
													name="supplies_travel_activities"
													label="Travel Activities"
												/>
											</Box>
										</FeatureFlag>
									</>
								) : null}
								{hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) ? (
									<Box
										paddingY="1"
										paddingX="4"
										borderWidth="1"
										rounded="md"
										bgColor="subtle"
									>
										<SwitchInputField name="supplies_hotels" label="Hotels" />
									</Box>
								) : null}
							</Inline>
						</Stack>

						<TextInputField type="text" name="name" label="Supplier Name" />
					</Stack>
					<Divider />
					<SubmissionError />
					<Inline gap="4" paddingTop={"1"}>
						<Button type="submit" disabled={submitting}>
							{submitting ? "Updating..." : "Update Details"}
						</Button>
						{onCancel ? (
							<Button onClick={() => onCancel()} disabled={submitting}>
								Cancel
							</Button>
						) : null}
					</Inline>
				</form>
			)}
		</Form>
	)
}

type AddPaymentPreferenceInDialogProps = {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
	supplier: ISupplier
}

export function AddPaymentPreferenceInDialog({
	children,
	onSuccess,
	...props
}: Readonly<AddPaymentPreferenceInDialogProps>) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} title="Add Payment Preference" md>
				<Dialog.Body>
					<AddPaymentPreference
						{...props}
						onSuccess={() => {
							onSuccess()
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type AddPaymentPreferenceProps = {
	onSuccess: (data: PaymentPreferencePayload) => void
	onCancel: () => void
	supplier: ISupplier
}

export function AddPaymentPreference({
	onSuccess,
	supplier,
	...props
}: Readonly<AddPaymentPreferenceProps>) {
	const { hasPermission } = useCheckPermissions()
	const canViewTransport = hasPermission(
		PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
	)
	const canViewHotel = hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS)
	const xhr = useXHR()
	const initialValues: PaymentPreferenceSchema = useMemo(() => {
		const {
			supplies_cabs,
			supplies_travel_activities,
			supplies_hotels,
			activity_supplier_payment_preference,
			hotel_supplier_payment_preference,
			transport_supplier_payment_preference,
		} = supplier
		const result: PaymentPreferenceSchema = {
			...(canViewTransport && supplies_cabs
				? {
						transport_supplier_payment_preference:
							transport_supplier_payment_preference ?? null,
					}
				: {}),
			...(canViewTransport && supplies_travel_activities
				? {
						activity_supplier_payment_preference:
							activity_supplier_payment_preference ?? null,
					}
				: {}),
			...(canViewHotel && supplies_hotels
				? {
						hotel_supplier_payment_preference:
							hotel_supplier_payment_preference ?? null,
					}
				: {}),
			ui: {
				showCabPreferences: supplies_cabs,
				showActivitiesPreferences: supplies_travel_activities,
				showHotelPreferences: supplies_hotels,
			},
		}
		return result
	}, [canViewHotel, canViewTransport, supplier])
	return (
		<PaymentPreferenceForm
			{...props}
			initialValues={initialValues}
			onSubmit={async (data) => {
				await XHR(xhr).savePaymentPreferance({ id: supplier.id, data })
				onSuccess(data)
			}}
		/>
	)
}

type UpdatePaymentPreferenceInDialogProps = {
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess: () => void
	supplier: ISupplier
}

export function UpdatePaymentPreferenceInDialog({
	children,
	onSuccess,
	...props
}: Readonly<UpdatePaymentPreferenceInDialogProps>) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} title="Edit Payment Preference" md>
				<Dialog.Body>
					<UpdatePaymentPreference
						{...props}
						onSuccess={() => {
							onSuccess()
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type UpdatePaymentPreferenceProps = {
	onSuccess: (data: PaymentPreferencePayload) => void
	onCancel: () => void
	supplier: ISupplier
}

export function UpdatePaymentPreference({
	onSuccess,
	supplier,
	...props
}: Readonly<UpdatePaymentPreferenceProps>) {
	const { hasPermission } = useCheckPermissions()
	const canViewTransport = hasPermission(
		PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
	)
	const canViewHotel = hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS)
	const xhr = useXHR()
	const initialValues: PaymentPreferenceSchema = useMemo(() => {
		const {
			supplies_cabs,
			supplies_travel_activities,
			supplies_hotels,
			activity_supplier_payment_preference,
			hotel_supplier_payment_preference,
			transport_supplier_payment_preference,
		} = supplier
		const result: PaymentPreferenceSchema = {
			...(canViewTransport && supplies_cabs
				? {
						transport_supplier_payment_preference:
							transport_supplier_payment_preference ?? null,
					}
				: {}),
			...(canViewTransport && supplies_travel_activities
				? {
						activity_supplier_payment_preference:
							activity_supplier_payment_preference ?? null,
					}
				: {}),
			...(canViewHotel && supplies_hotels
				? {
						hotel_supplier_payment_preference:
							hotel_supplier_payment_preference ?? null,
					}
				: {}),
			ui: {
				showCabPreferences: supplies_cabs,
				showActivitiesPreferences: supplies_travel_activities,
				showHotelPreferences: supplies_hotels,
			},
		}
		return result
	}, [canViewHotel, canViewTransport, supplier])
	return (
		<PaymentPreferenceForm
			{...props}
			initialValues={initialValues}
			onSubmit={async (data) => {
				await XHR(xhr).savePaymentPreferance({ id: supplier.id, data })
				onSuccess(data)
			}}
		/>
	)
}

type PaymentPreferenceSchema = {
	transport_supplier_payment_preference?: IPaymentPreference | null
	activity_supplier_payment_preference?: IPaymentPreference | null
	hotel_supplier_payment_preference?: IPaymentPreference | null
	ui: {
		showCabPreferences: boolean
		showActivitiesPreferences: boolean
		showHotelPreferences: boolean
	}
}

type PaymentPreferenceFormProps = {
	initialValues: PaymentPreferenceSchema
	onSubmit: (data: PaymentPreferencePayload) => Promise<void>
	onCancel?: () => void
}

function PaymentPreferenceForm({
	initialValues,
	onSubmit,
	onCancel,
}: Readonly<PaymentPreferenceFormProps>) {
	const { hasPermission } = useCheckPermissions()
	const canViewTransport = hasPermission(
		PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
	)
	const canViewHotel = hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS)
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				const { ui } = values
				const {
					showActivitiesPreferences,
					showCabPreferences,
					showHotelPreferences,
				} = ui

				const payload: PaymentPreferencePayload = {
					...(canViewTransport && showActivitiesPreferences
						? {
								activity_supplier_payment_preference:
									values?.activity_supplier_payment_preference?.id ?? null,
							}
						: {}),
					...(canViewTransport && showCabPreferences
						? {
								transport_supplier_payment_preference:
									values.transport_supplier_payment_preference?.id ?? null,
							}
						: {}),
					...(canViewHotel && showHotelPreferences
						? {
								hotel_supplier_payment_preference:
									values.hotel_supplier_payment_preference?.id ?? null,
							}
						: {}),
				}
				await onSubmit(payload)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Inline gap="2" flexWrap="wrap">
						<GetFieldValue<PaymentPreferenceSchema["ui"]> name="ui">
							{({ value: ui }) => (
								<Grid>
									{canViewTransport && ui.showCabPreferences ? (
										<Col xs={12} md={6} paddingY="2">
											<SelectField
												select={SelectTransportPaymentPreferences}
												name="transport_supplier_payment_preference"
												label="Cab Payment Preferences"
												required
												fetchOnMount
												multiple={false}
												placeholder="Type to search..."
											/>
										</Col>
									) : null}
									{canViewTransport && ui.showActivitiesPreferences ? (
										<Col xs={12} md={6} paddingY="2">
											<SelectField
												select={SelectActivityPaymentPreferences}
												name="activity_supplier_payment_preference"
												label="Activities Payment Preferences"
												required
												fetchOnMount
												multiple={false}
												placeholder="Type to search..."
											/>
										</Col>
									) : null}
									{canViewHotel && ui.showHotelPreferences ? (
										<Col xs={12} md={6} paddingY="2">
											<SelectField
												select={SelectHotelPaymentPreferences}
												name="hotel_supplier_payment_preference"
												label="Hotel Payment Preferences"
												required
												fetchOnMount
												multiple={false}
												placeholder="Type to search..."
											/>
										</Col>
									) : null}
								</Grid>
							)}
						</GetFieldValue>
					</Inline>
					<Divider sm />
					<SubmissionError />
					<Inline gap="4">
						<Button type="submit" disabled={submitting}>
							{submitting ? "Saving..." : "Save Details"}
						</Button>
						{onCancel ? (
							<Button onClick={() => onCancel()} disabled={submitting}>
								Cancel
							</Button>
						) : null}
					</Inline>
				</form>
			)}
		</Form>
	)
}

type CabPaymentPreferencesFormProps = {
	supplierId: number
}

export function TransportPaymentPreferencesForm({
	supplierId,
}: Readonly<CabPaymentPreferencesFormProps>) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { supplier, revalidate, isLoading, isValidating } = useSupplier({
		supplierId,
	})

	const paymentPreference: IPaymentPreference | null | undefined =
		useMemo(() => {
			return supplier?.transport_supplier_payment_preference
		}, [supplier?.transport_supplier_payment_preference])

	if (isLoading || isValidating) return <Spinner padding="4" />

	return (
		<Stack gap="1">
			<Text
				textTransform="uppercase"
				letterSpacing="wider"
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
			>
				Payment Preference
			</Text>
			<Box fontSize="md">
				{paymentPreference ? (
					<Inline justifyContent="between" gap="2">
						<Inline gap="4">
							<Text fontSize="md" fontWeight="semibold">
								{paymentPreference.name}
							</Text>
						</Inline>
						<Box>
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary" size="sm">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<Dropdown.MenuItem onClick={openDialog}>
										<Icons.Pencil /> Edit
									</Dropdown.MenuItem>
								</Dropdown.Menu>
							</Dropdown>
						</Box>
					</Inline>
				) : (
					<Stack gap="2">
						<Text fontSize="md" color="muted" fontWeight="semibold">
							Not Set
						</Text>
						<Text fontWeight="normal" fontSize="md">
							You should set payment preference for the cab supplier to easily
							create instalment accordingly.
						</Text>
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
							<Box>
								<Button onClick={openDialog} size="sm" level="primary">
									Set Payment Preference
								</Button>
							</Box>
						) : null}
					</Stack>
				)}
				<Dialog
					open={isDialogOpen}
					onClose={closeDialog}
					title="Set Payment Preference"
					sm
				>
					<Form<{
						transport_supplier_payment_preference:
							| IPaymentPreference
							| null
							| undefined
					}>
						initialValues={{
							transport_supplier_payment_preference: paymentPreference,
						}}
						onSubmit={withServerErrors(async (values) => {
							const payload: PaymentPreferencePayload = {
								transport_supplier_payment_preference:
									values.transport_supplier_payment_preference?.id ?? null,
							}
							await XHR(xhr).savePaymentPreferance({
								id: supplierId,
								data: payload,
							})
							revalidate()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Dialog.Body>
									<Stack gap="4">
										<Box>
											<SelectField
												name="transport_supplier_payment_preference"
												select={SelectTransportPaymentPreferences}
												label="Select Payment Preference"
												fetchOnMount
												multiple={false}
											/>
										</Box>
										<SubmissionError />
									</Stack>
								</Dialog.Body>
								<Dialog.Footer>
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save"}
									</Button>
									<Button disabled={submitting} onClick={closeDialog}>
										Cancel
									</Button>
								</Dialog.Footer>
							</form>
						)}
					</Form>
				</Dialog>
			</Box>
		</Stack>
	)
}

type ActivityPaymentPreferencesFormProps = {
	supplierId: number
}
export function ActivityPaymentPreferencesForm({
	supplierId,
}: Readonly<ActivityPaymentPreferencesFormProps>) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { supplier, revalidate, isLoading, isValidating } = useSupplier({
		supplierId,
	})
	const paymentPreference: IPaymentPreference | null | undefined =
		useMemo(() => {
			return supplier?.activity_supplier_payment_preference
		}, [supplier?.activity_supplier_payment_preference])

	if (isLoading || isValidating) return <Spinner padding="4" />

	return (
		<Stack gap="1">
			<Text
				textTransform="uppercase"
				letterSpacing="wider"
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
			>
				Payment Preference
			</Text>
			<Box fontSize="md">
				{paymentPreference ? (
					<Inline justifyContent="between" gap="2">
						<Inline gap="4">
							<Text fontSize="md" fontWeight="semibold">
								{paymentPreference.name}
							</Text>
						</Inline>
						<Box>
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary" size="sm">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<Dropdown.MenuItem onClick={openDialog}>
										<Icons.Pencil /> Edit
									</Dropdown.MenuItem>
								</Dropdown.Menu>
							</Dropdown>
						</Box>
					</Inline>
				) : (
					<Stack gap="2">
						<Text fontSize="md" color="muted" fontWeight="semibold">
							Not Set
						</Text>
						<Text fontWeight="normal" fontSize="md">
							You should set payment preference for the activity supplier to
							easily create instalment accordingly.
						</Text>
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
							<Box>
								<Button onClick={openDialog} size="sm" level="primary">
									Set Payment Preference
								</Button>
							</Box>
						) : null}
					</Stack>
				)}
				<Dialog
					open={isDialogOpen}
					onClose={closeDialog}
					title="Set Payment Preference"
					sm
				>
					<Form<{
						activity_supplier_payment_preference:
							| IPaymentPreference
							| null
							| undefined
					}>
						initialValues={{
							activity_supplier_payment_preference: paymentPreference,
						}}
						onSubmit={withServerErrors(async (values) => {
							const payload: PaymentPreferencePayload = {
								activity_supplier_payment_preference:
									values.activity_supplier_payment_preference?.id ?? null,
							}
							await XHR(xhr).savePaymentPreferance({
								id: supplierId,
								data: payload,
							})
							revalidate()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Dialog.Body>
									<Stack gap="4">
										<Box>
											<SelectField
												name="activity_supplier_payment_preference"
												select={SelectActivityPaymentPreferences}
												label="Select Payment Preference"
												fetchOnMount
												multiple={false}
											/>
										</Box>
										<SubmissionError />
									</Stack>
								</Dialog.Body>
								<Dialog.Footer>
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save"}
									</Button>
									<Button disabled={submitting} onClick={closeDialog}>
										Cancel
									</Button>
								</Dialog.Footer>
							</form>
						)}
					</Form>
				</Dialog>
			</Box>
		</Stack>
	)
}

type HotelPaymentPreferencesFormProps = {
	supplierId: number
	onChange: () => void
}

export function HotelPaymentPreferencesForm({
	supplierId,
	onChange,
}: Readonly<HotelPaymentPreferencesFormProps>) {
	const { hasPermission } = useCheckPermissions()
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { supplier, revalidate, isLoading, isValidating } = useSupplier({
		supplierId,
	})

	const paymentPreference: IPaymentPreference | null | undefined =
		useMemo(() => {
			return supplier?.hotel_supplier_payment_preference
		}, [supplier?.hotel_supplier_payment_preference])

	if (isLoading || isValidating) return <Spinner padding="4" />

	return (
		<Stack gap="1">
			<Text
				textTransform="uppercase"
				letterSpacing="wider"
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
			>
				Payment Preference
			</Text>
			<Box fontSize="md">
				{paymentPreference ? (
					<Inline justifyContent="between" gap="2">
						<Inline gap="4">
							<Text fontSize="md" fontWeight="semibold">
								{paymentPreference.name}
							</Text>
						</Inline>
						<Box>
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary" size="sm">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<Dropdown.MenuItem onClick={openDialog}>
										<Icons.Pencil /> Edit
									</Dropdown.MenuItem>
								</Dropdown.Menu>
							</Dropdown>
						</Box>
					</Inline>
				) : (
					<Stack gap="2">
						<Text fontSize="md" color="muted" fontWeight="semibold">
							Not Set
						</Text>
						<Text fontWeight="normal" fontSize="md">
							You should set payment preference for the hotel supplier to easily
							create instalment accordingly.
						</Text>
						{hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) ? (
							<Box>
								<Button onClick={openDialog} size="sm" level="primary">
									Set Payment Preference
								</Button>
							</Box>
						) : null}
					</Stack>
				)}
				<Dialog
					open={isDialogOpen}
					onClose={closeDialog}
					title="Set Payment Preference"
					sm
				>
					<Form<{
						hotel_supplier_payment_preference:
							| IPaymentPreference
							| null
							| undefined
					}>
						initialValues={{
							hotel_supplier_payment_preference: paymentPreference,
						}}
						onSubmit={withServerErrors(async (values) => {
							const payload: PaymentPreferencePayload = {
								hotel_supplier_payment_preference:
									values.hotel_supplier_payment_preference?.id ?? null,
							}
							await XHR(xhr).savePaymentPreferance({
								id: supplierId,
								data: payload,
							})
							closeDialog()
							onChange()
							revalidate()
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Dialog.Body>
									<Stack gap="4">
										<Box>
											<SelectField
												name="hotel_supplier_payment_preference"
												select={SelectHotelPaymentPreferences}
												label="Select Payment Preference"
												fetchOnMount
												multiple={false}
											/>
										</Box>
										<SubmissionError />
									</Stack>
								</Dialog.Body>
								<Dialog.Footer>
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving..." : "Save"}
									</Button>
									<Button disabled={submitting} onClick={closeDialog}>
										Cancel
									</Button>
								</Dialog.Footer>
							</form>
						)}
					</Form>
				</Dialog>
			</Box>
		</Stack>
	)
}
